import { errorToast } from '@/utils';
import {
  Button,
  Flex,
  Form,
  Input,
  InputNumber,
  Skeleton,
  TimePicker,
} from 'antd';
import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import { useEffect } from 'react';
import {
  useCreateShiftMutation,
  useGetShiftDetailsQuery,
  useUpdateShiftMutation,
} from '../../../../../../app/features/employee/emp-attendance/emp-attendance.api';
import useAuth from '../../../../../../hooks/useAuth';
import { toast } from '../../../../../../lib/toast';
import TimeIcon from './_icons/TimeIcon';

interface ShiftFormValues {
  name: string;
  startTime: Dayjs;
  endTime: Dayjs;
  checkInGracePeriod: number;
}

type Props = {
  shiftId?: string;
};

export default function ShiftAdd({ shiftId }: Props) {
  const [form] = Form.useForm<ShiftFormValues>();
  const { organizationId, tenantId } = useAuth();

  const [createShift, { isLoading: isCreating }] = useCreateShiftMutation();
  const [updateShift, { isLoading: isUpdating }] = useUpdateShiftMutation();

  // Fetch shift details if shiftId is provided
  const { data: shiftDetails, isLoading: isFetching } = useGetShiftDetailsQuery(
    shiftId!,
    { skip: !shiftId },
  );

  const isEditMode = !!shiftId;
  const isLoading = isCreating || isUpdating;

  // Populate form when editing
  useEffect(() => {
    if (isEditMode && shiftDetails?.data) {
      const shift = shiftDetails.data;
      form.setFieldsValue({
        name: shift.name,
        startTime: dayjs(shift.startTime, 'HH:mm'),
        endTime: dayjs(shift.endTime, 'HH:mm'),
        checkInGracePeriod: shift.checkInGracePeriod,
      });
    }
  }, [isEditMode, shiftDetails, form]);

  const handleSubmit = async (values: ShiftFormValues) => {
    try {
      const payload = {
        name: values.name,
        tenantId,
        organizationId,
        startTime: values.startTime.format('HH:mm'),
        endTime: values.endTime.format('HH:mm'),
        checkInGracePeriod: values.checkInGracePeriod,
      };

      if (isEditMode) {
        await updateShift({ id: shiftId, data: payload }).unwrap();
        toast('success', 'Shift updated successfully');
      } else {
        await createShift(payload).unwrap();
        toast('success', 'Shift created successfully');
        form.resetFields();
      }
    } catch (err) {
      errorToast(
        err,
        isEditMode
          ? 'Failed to update shift. Please try again'
          : 'Failed to create shift. Please try again',
      );
    }
  };

  if (isEditMode && isFetching) {
    return (
      <div className="space-y-6">
        <Skeleton.Input
          active
          size="large"
          block
          className="!rounded-xl !h-12"
        />
        <div className="grid grid-cols-3 gap-4">
          <Skeleton.Input
            active
            size="large"
            block
            className="!rounded-xl !h-12"
          />
          <Skeleton.Input
            active
            size="large"
            block
            className="!rounded-xl !h-12"
          />
          <Skeleton.Input
            active
            size="large"
            block
            className="!rounded-xl !h-12"
          />
        </div>
        <Flex justify="end">
          <Skeleton.Button active size="large" className="!rounded-xl !w-24" />
        </Flex>
      </div>
    );
  }

  return (
    <Form
      form={form}
      size="large"
      layout="vertical"
      className="custom-form"
      onFinish={handleSubmit}
    >
      <Form.Item
        label="Shift Name"
        name="name"
        rules={[{ required: true, message: 'Please enter shift name' }]}
      >
        <Input className="!rounded-xl" />
      </Form.Item>

      <div className="grid grid-cols-3 gap-4 mt-2 mb-6">
        <Form.Item
          label="Start Time"
          name="startTime"
          rules={[{ required: true, message: 'Please select start time' }]}
        >
          {/* <DatePicker
            picker="time"
            placeholder="HH:MM:SS"
            format="hh:mm:ss A"
            use12Hours={true}
            className="!rounded-xl w-full"
          /> */}
          <TimePicker
            placeholder="HH:MM:SS"
            format="hh:mm:ss A"
            use12Hours={true}
            showNow={false}
            inputReadOnly={false}
            needConfirm={false}
            className="!rounded-xl w-full"
          />
        </Form.Item>

        <Form.Item
          label="End Time"
          name="endTime"
          rules={[{ required: true, message: 'Please select end time' }]}
        >
          {/* <DatePicker
            picker="time"
            placeholder="HH:MM:SS"
            format="hh:mm:ss A"
            use12Hours={true}
            className="!rounded-xl w-full"
            
          /> */}

          <TimePicker
            placeholder="HH:MM:SS"
            format="hh:mm:ss A"
            use12Hours={true}
            showNow={false}
            inputReadOnly={false}
            needConfirm={false}
            className="!rounded-xl w-full"
          />
        </Form.Item>

        <Form.Item
          label="Grace Period"
          name="checkInGracePeriod"
          validateFirst
          rules={[
            { required: true, message: 'Please select grace period' },
            {
              validator: (_, value) => {
                if (value && value <= 0) {
                  return Promise.reject(
                    new Error('Grace period must be greater than 0'),
                  );
                }
                return Promise.resolve();
              },
            },
          ]}
        >
          <InputNumber
            placeholder="Min"
            className="!rounded-xl w-full"
            min={1}
            max={1440}
            suffix={<TimeIcon />}
          />
        </Form.Item>
      </div>

      <Flex justify="end" gap="small">
        <Button
          type="primary"
          size="large"
          htmlType="submit"
          loading={isLoading}
          className="btn-primary !bg-transparent hover:!bg-transparent !text-theme-primary hover:!text-theme-primary !text-base"
        >
          {isEditMode ? 'Update' : 'Add'}
        </Button>
      </Flex>
    </Form>
  );
}
